import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';
import { MOCK_METRICS, MOCK_ACTIONS, MOCK_DECISIONS } from '../data/mockData';
import { useNavigate } from 'react-router-dom';

const { FiClock, FiCheckCircle, FiTarget, FiActivity, FiArrowRight, FiVideo } = FiIcons;

const StatCard = ({ title, value, icon, colorClass, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="glass-panel p-6 rounded-xl relative overflow-hidden group"
  >
    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${colorClass} opacity-10 rounded-bl-full transition-transform duration-500 group-hover:scale-110`}></div>
    <div className="flex justify-between items-start relative z-10">
      <div>
        <h3 className="text-gray-400 text-xs font-mono uppercase tracking-wider mb-2">{title}</h3>
        <div className="text-3xl font-bold text-white">{value}</div>
      </div>
      <div className={`p-3 rounded-lg bg-white/5 border border-white/10 ${colorClass.replace('from-', 'text-').split(' ')[0]}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
    </div>
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();

  const chartOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(26, 11, 46, 0.9)',
      borderColor: 'rgba(0, 240, 255, 0.3)',
      textStyle: { color: '#fff' },
      axisPointer: { type: 'cross', label: { backgroundColor: '#6a7985' } }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
        axisLabel: { color: '#9ca3af', fontFamily: 'JetBrains Mono' }
      }
    ],
    yAxis: [
      {
        type: 'value',
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
        axisLabel: { color: '#9ca3af', fontFamily: 'JetBrains Mono' }
      }
    ],
    series: [
      {
        name: 'Hours Logged',
        type: 'line',
        smooth: true,
        lineStyle: { width: 3, shadowColor: 'rgba(0,240,255,0.5)', shadowBlur: 10, color: '#00F0FF' },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(0, 240, 255, 0.3)' },
              { offset: 1, color: 'rgba(0, 240, 255, 0)' }
            ]
          }
        },
        data: [2.5, 4.2, 3.8, 5.5, 2.1, 0.5, 0]
      }
    ]
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Command Center</h1>
          <p className="text-gray-400 text-sm mt-1">System intelligence overview for current cycle.</p>
        </div>
        <button 
          onClick={() => navigate('/library')}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-400/10 text-cyan-400 border border-cyan-400/50 rounded-lg hover:bg-cyan-400/20 hover:shadow-neon transition-all font-mono text-sm"
        >
          <SafeIcon icon={FiActivity} />
          <span>Launch Console</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Meetings" value={MOCK_METRICS.totalMeetings} icon={FiVideo} colorClass="from-violet-500 to-purple-600" delay={0.1} />
        <StatCard title="Hours Saved" value={`${MOCK_METRICS.hoursSaved}h`} icon={FiClock} colorClass="from-cyan-400 to-blue-500" delay={0.2} />
        <StatCard title="Pending Actions" value={MOCK_METRICS.actionsPending} icon={FiCheckCircle} colorClass="from-amber-400 to-orange-500" delay={0.3} />
        <StatCard title="Decisions Logged" value={MOCK_METRICS.decisionsLogged} icon={FiTarget} colorClass="from-emerald-400 to-green-500" delay={0.4} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="lg:col-span-2 glass-panel p-6 rounded-xl flex flex-col"
        >
          <h3 className="text-sm font-mono text-gray-400 uppercase tracking-wider mb-4">Meeting Volume Analysis</h3>
          <div className="flex-1 min-h-[300px]">
            <ReactECharts option={chartOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="glass-panel p-6 rounded-xl flex flex-col"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-mono text-gray-400 uppercase tracking-wider">Priority Stream</h3>
            <button className="text-cyan-400 text-xs hover:text-cyan-300">View All</button>
          </div>
          
          <div className="space-y-4 flex-1 overflow-y-auto scrollbar-custom pr-2">
            {MOCK_ACTIONS.filter(a => a.status === 'pending').slice(0, 4).map((action, i) => (
              <div key={action.id} className="p-3 rounded-lg bg-white/5 border border-white/5 hover:border-cyan-400/30 transition-colors group cursor-pointer">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs text-cyan-400 font-mono tracking-tight">{action.meeting}</span>
                  <span className="text-[10px] text-gray-500 bg-black/30 px-2 py-0.5 rounded">{action.due}</span>
                </div>
                <p className="text-sm text-gray-200 mb-2">{action.task}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-violet-800 flex items-center justify-center text-[10px] font-bold border border-violet-600">
                      {action.assignee.charAt(0)}
                    </div>
                    <span className="text-xs text-gray-400">{action.assignee}</span>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="text-gray-600 group-hover:text-cyan-400 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}